import moment from 'moment'

/**
 * Formatting for the card-detail Activity tab (EX-40600).
 *
 * The contract hands us `occurredAt` as a Unix epoch in MILLISECONDS and says
 * explicitly that clients derive the relative label ("2 days ago") from their
 * own locale and clock — the server never sends display text for time. That is
 * the whole job here.
 *
 * `moment(number)` is the epoch-milliseconds constructor. Do NOT pass the value
 * through `moment(String(occurredAt))` or an ISO helper: the statements
 * formatters next door take ISO-8601 strings, and reaching for one of those out
 * of habit parses the epoch as a year and renders every row as decades away.
 */

/**
 * "2 days ago", localized by the active moment locale.
 *
 * Sub-minute events render as "a few seconds ago" from `fromNow()`, which reads
 * oddly against a freshly appended row, so they collapse to the design's
 * new-event label instead.
 *
 * @param justNowLabel - The translated "Just now" string. Passed in rather than
 *   hardcoded because this module has no `translate`, and a literal here would
 *   be the one English word left in an otherwise localized row.
 */
export const formatActivityTimestamp = (
  occurredAtMs: number,
  justNowLabel: string,
): string => {
  const occurredAt = moment(occurredAtMs)

  if (moment().diff(occurredAt, 'seconds') < 60) {
    return justNowLabel
  }

  return occurredAt.fromNow()
}

/**
 * The full timestamp, for the `title` of the relative label.
 *
 * The relative label alone is lossy — "2 days ago" cannot be reconciled against
 * an audit log or a support ticket, and this feed is audit history. The exact
 * time stays available on hover and to assistive tech without adding a second
 * visible line the design does not have.
 *
 * `lll` is moment's LOCALIZED long-date token, and both halves of that matter.
 * A hand-built pattern like `MMM D, YYYY [at] h:mm A` escapes the English word
 * "at" into a string users actually read — the one untranslated word in an
 * otherwise localized row, and invisible to the localization gate because it
 * sits inside a format string rather than JSX. It also pins US date order and a
 * 12-hour clock for every locale. `lll` carries neither problem: month name,
 * field order, and clock convention all follow the locale moment is already set
 * to in `App.tsx`.
 */
export const formatActivityTimestampExact = (occurredAtMs: number): string =>
  moment(occurredAtMs).format('lll')

/** ISO-8601, for the `<time dateTime>` attribute — a machine-readable format. */
export const toActivityDateTimeAttribute = (occurredAtMs: number): string =>
  moment(occurredAtMs).toISOString()
